import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { WppChat } from "@/hooks/useWhatsAppChat";
import { useBulkCampaign } from "@/context/BulkCampaignContext";


interface BulkContact {
  id: string;
  name: string;
  phone: string;
  resolvedPhone?: string;
  status: "pending" | "sent" | "failed";
  selected: boolean;
  source: "chat" | "csv";
  csvData?: Record<string, string>;
  isGroup?: boolean;
}

interface BulkMessagingPanelProps {
  chats: WppChat[];
  csvData: Record<string, string>[];
  sendMessage: (phone: string, text: string, isGroup?: boolean) => Promise<boolean>;
  sendImage: (phone: string, base64: string, filename: string, caption: string, isGroup?: boolean) => Promise<boolean>;
  sendFile: (phone: string, base64: string, filename: string, caption: string, isGroup?: boolean) => Promise<boolean>;
  resolveLidToPhone: (lidJid: string) => Promise<string | null>;
  onBack: () => void;
}

function guessCountryCode(chats: WppChat[]): string {
  for (const chat of chats) {
    if (chat.isGroup) continue;
    const cleanUser = chat.id.user.replace(/[^0-9]/g, "");
    if (cleanUser.length >= 10 && cleanUser.length <= 15) {
      if (cleanUser.startsWith("20")) return "20";
      if (cleanUser.startsWith("966")) return "966";
      if (cleanUser.startsWith("971")) return "971";
      if (cleanUser.startsWith("965")) return "965";
      if (cleanUser.startsWith("973")) return "973";
      if (cleanUser.startsWith("968")) return "968";
      if (cleanUser.startsWith("964")) return "964";
      if (cleanUser.startsWith("962")) return "962";
      if (cleanUser.startsWith("961")) return "961";
      if (cleanUser.startsWith("970")) return "970";
      if (cleanUser.startsWith("212")) return "212";
      if (cleanUser.startsWith("213")) return "213";
      if (cleanUser.startsWith("216")) return "216";
      if (cleanUser.startsWith("218")) return "218";
      if (cleanUser.startsWith("249")) return "249";
      if (cleanUser.startsWith("1")) return "1";
      if (cleanUser.startsWith("44")) return "44";
    }
  }
  return "20";
}

function formatPhoneNumber(phone: string, defaultCountryCode: string): string {
  let clean = phone.replace(/[^0-9]/g, "");
  if (clean.startsWith("00")) {
    clean = clean.slice(2);
  }
  if (clean.startsWith("0")) {
    clean = defaultCountryCode + clean.slice(1);
  }
  if (!clean.startsWith(defaultCountryCode) && clean.length <= 10) {
    clean = defaultCountryCode + clean;
  }
  return clean;
}

function findPhoneKey(row: Record<string, string>): string | undefined {
  const keys = Object.keys(row);
  // Exact match first
  const exact = keys.find((k) => {
    const lower = k.toLowerCase().trim();
    return lower === "phone" || lower === "mobile" || lower === "tel" || lower === "number" || lower === "cell" || lower === "contact" || lower === "phone_number" || lower === "mobile_number" || lower === "phone number" || lower === "mobile number";
  });
  if (exact) return exact;
  // Fallback to partial match
  return keys.find((k) => {
    const lower = k.toLowerCase().trim();
    return (
      lower.includes("phone") ||
      lower.includes("mobile") ||
      lower.includes("tel") ||
      lower.includes("number") ||
      lower.includes("contact") ||
      lower.includes("cell") ||
      lower.includes("phone_number") ||
      lower.includes("mobile_number")
    );
  });
}

function findPhoneValue(row: Record<string, string>): string {
  const key = findPhoneKey(row);
  if (key) {
    const val = row[key].replace(/[^0-9]/g, "");
    if (val) return val;
  }
  // Fallback: scan all values for something that looks like a phone number
  for (const k of Object.keys(row)) {
    const v = row[k].trim();
    if (!v) continue;
    // Remove + and spaces, check if mostly digits and at least 7 chars
    const digits = v.replace(/[^0-9]/g, "");
    if (digits.length >= 7 && digits.length >= v.replace(/[^0-9+]/g, "").length * 0.5) {
      return digits;
    }
  }
  return "";
}

function findNameKey(row: Record<string, string>): string | undefined {
  const keys = Object.keys(row);
  // Exact match first
  const exact = keys.find((k) => {
    const lower = k.toLowerCase().trim();
    return lower === "name" || lower === "first_name" || lower === "last_name" || lower === "customer_name" || lower === "full_name" || lower === "display_name" || lower === "first name" || lower === "last name" || lower === "customer name" || lower === "full name" || lower === "display name";
  });
  if (exact) return exact;
  // Fallback to partial match
  return keys.find((k) => {
    const lower = k.toLowerCase().trim();
    return (
      lower === "name" ||
      lower.includes("first_name") ||
      lower.includes("last_name") ||
      lower.includes("customer_name") ||
      lower.includes("full_name") ||
      lower.includes("display_name") ||
      lower.includes("first name") ||
      lower.includes("last name") ||
      lower.includes("customer name") ||
      lower.includes("full name") ||
      lower.includes("display name")
    );
  });
}

function parseCSV(text: string): { rows: Record<string, string>[]; errors: string[] } {
  const errors: string[] = [];

  // Normalize line endings
  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalized.split("\n").filter((l) => l.trim());

  if (lines.length === 0) {
    errors.push("File is empty.");
    return { rows: [], errors };
  }

  // Detect delimiter: if any line contains a tab, use tab; otherwise comma
  const hasTabs = lines.some((l) => l.includes("\t"));
  const delimiter = hasTabs ? "\t" : ",";

  const parseLine = (line: string): string[] => {
    if (delimiter === "\t") {
      // Simple tab split \u2014 no quoting needed for TSV
      return line.split("\t").map((v) => v.trim());
    }
    // CSV with quoted fields
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (i + 1 < line.length && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          current += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ',') {
          result.push(current.trim());
          current = "";
        } else {
          current += ch;
        }
      }
    }
    result.push(current.trim());
    return result;
  };

  // Recognizable phone header keywords
  const phoneKeywords = ["phone", "mobile", "tel", "number", "contact", "cell", "phone number", "mobile number", "cell phone"];
  // Recognizable name header keywords
  const nameKeywords = ["name", "first_name", "last_name", "customer_name", "full_name", "display_name", "first name", "last name"];

  const hasHeaderRow = (line: string): boolean => {
    const cells = parseLine(line);
    const lowerCells = cells.map((c) => c.toLowerCase());
    return lowerCells.some((c) => phoneKeywords.some((k) => c.includes(k))) ||
           lowerCells.some((c) => nameKeywords.some((k) => c.includes(k)));
  };

  // Find the actual header row (skip title rows like "Subscriptions")
  let headerIdx = 0;
  if (!hasHeaderRow(lines[0]) && lines.length > 1) {
    const idx = lines.findIndex((l, i) => i > 0 && hasHeaderRow(l));
    if (idx !== -1) {
      headerIdx = idx;
    }
  }

  const headers = parseLine(lines[headerIdx]);
  if (headers.length === 0 || headers.every((h) => !h)) {
    errors.push("No valid headers found.");
    return { rows: [], errors };
  }

  if (lines.length <= headerIdx + 1) {
    errors.push("File has headers only — no data rows found.");
    return { rows: [], errors };
  }

  const rows: Record<string, string>[] = [];
  lines.slice(headerIdx + 1).forEach((line, lineIdx) => {
    const values = parseLine(line);
    if (values.length !== headers.length) {
      errors.push(
        `Row ${lineIdx + 2}: expected ${headers.length} columns, got ${values.length}. Row skipped.`,
      );
      return;
    }
    const row: Record<string, string> = {};
    headers.forEach((h, i) => {
      row[h] = values[i] || "";
    });
    rows.push(row);
  });

  return { rows, errors };
}

const statusColors: Record<string, string> = {
  pending: "bg-secondary-100 text-secondary-700",
  sent: "bg-accent-100 text-accent-700",
  failed: "bg-red-50 text-red-600",
};

export default function BulkMessagingPanel({
  chats,
  csvData,
  sendMessage,
  sendImage,
  sendFile,
  resolveLidToPhone,
  onBack,
}: BulkMessagingPanelProps) {
  const { t: tt } = useTranslation("dashboard");
  const {
    contacts,
    setContacts,
    isSending,
    isScheduled,
    sendProgress,
    error: contextError,
    scheduleDate: contextScheduleDate,
    scheduleTime: contextScheduleTime,
    campaignConfig,
    startCampaign,
    scheduleCampaign,
    cancelSchedule,
    stopCampaign,
  } = useBulkCampaign();

  const [messageTemplate, setMessageTemplate] = useState("");
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState("");
  const [isImage, setIsImage] = useState(false);
  const [delay, setDelay] = useState(5);
  const [randomDelay, setRandomDelay] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    "all" | "pending" | "sent" | "failed"
  >("all");
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [showSafeguardWarning, setShowSafeguardWarning] = useState(false);

  const [enableSchedule, setEnableSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [csvImportMsg, setCsvImportMsg] = useState<{ type: "success" | "warning" | "error"; text: string } | null>(null);
  const [mobileTab, setMobileTab] = useState<"composer" | "contacts">("composer");

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const ROW_HEIGHT = 44; // approximate row height in px

  // Sync running campaign settings to UI when active in background
  useEffect(() => {
    if ((isSending || isScheduled) && campaignConfig) {
      setMessageTemplate(campaignConfig.messageTemplate);
      setAttachedFile(campaignConfig.attachedFile);
      setFilePreview(campaignConfig.filePreview);
      setIsImage(campaignConfig.isImage);
      setDelay(campaignConfig.delay);
      setRandomDelay(campaignConfig.randomDelay);
      setEnableSchedule(isScheduled);
      if (isScheduled) {
        setScheduleDate(contextScheduleDate);
        setScheduleTime(contextScheduleTime);
      }
    }
  }, [isSending, isScheduled, campaignConfig, contextScheduleDate, contextScheduleTime]);

  // Dynamically calculate items per page based on table height
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const calculate = () => {
      const availableHeight = container.clientHeight - 42; // subtract thead height
      const count = Math.max(5, Math.floor(availableHeight / ROW_HEIGHT));
      setItemsPerPage(count);
    };

    calculate();
    const observer = new ResizeObserver(calculate);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Initialize contacts from chats and CSV (only if no active campaign in background)
  useEffect(() => {
    if (isSending || isScheduled) return;
    if (chats.length === 0 && csvData.length === 0) return;

    const defaultCountryCode = guessCountryCode(chats);

    setContacts((prev) => {
      const seenPhones = new Set(prev.map((c) => c.phone));
      const added: BulkContact[] = [];

      chats.forEach((chat) => {
        const phone = chat.id.user;
        if (!phone || seenPhones.has(phone)) return;
        seenPhones.add(phone);
        const name =
          chat.contact?.name ||
          chat.contact?.pushname ||
          chat.contact?.formattedName ||
          chat.contact?.shortName ||
          "N/A";
        added.push({
          id: `chat_${chat.id._serialized}`,
          name,
          phone,
          status: "pending",
          selected: true,
          source: "chat",
          isGroup: chat.isGroup,
        });
      });

      csvData.forEach((row, index) => {
        const nameKey = findNameKey(row);
        const rawPhone = findPhoneValue(row);
        const phone = formatPhoneNumber(rawPhone, defaultCountryCode);
        if (!phone || seenPhones.has(phone)) return;
        seenPhones.add(phone);
        const name = nameKey && row[nameKey]?.trim()
          ? row[nameKey].trim()
          : "N/A";
        added.push({
          id: `csv_${index}`,
          name,
          phone,
          status: "pending",
          selected: true,
          source: "csv",
          csvData: row,
          isGroup: false,
        });
      });

      if (added.length === 0) return prev;
      return [...prev, ...added];
    });
  }, [chats, csvData, isSending, isScheduled, setContacts]);



  const replaceVariables = useCallback(
    (template: string, contact: BulkContact): string => {
      return template
        .replace(/\{\{name\}\}/g, contact.name)
        .replace(/\{\{phone\}\}/g, contact.resolvedPhone || contact.phone);
    },
    [],
  );

  const insertVariable = useCallback(
    (variable: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = messageTemplate.slice(0, start);
      const after = messageTemplate.slice(end);
      const newText = `${before}${variable}${after}`;
      setMessageTemplate(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + variable.length,
          start + variable.length,
        );
      }, 0);
    },
    [messageTemplate],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setAttachedFile(file);
      setIsImage(file.type.startsWith("image/"));
      const reader = new FileReader();
      reader.onload = () => {
        setFilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      e.target.value = "";
    },
    [],
  );

  const handleCsvImport = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setCsvImportMsg(null);

      // Validate file type
      const validTypes = ["text/csv", "application/vnd.ms-excel", "text/plain"];
      const validExt = /\.csv$/i;
      if (!validTypes.includes(file.type) && !validExt.test(file.name)) {
        setCsvImportMsg({
          type: "error",
          text: tt("bulk.csvInvalidType"),
        });
        e.target.value = "";
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setCsvImportMsg({
          type: "error",
          text: tt("bulk.csvTooLarge", { size: (file.size / 1024 / 1024).toFixed(1) }),
        });
        e.target.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const text = reader.result as string;
        const { rows, errors } = parseCSV(text);

        // If there are parsing errors, show them
        if (errors.length > 0) {
          setCsvImportMsg({
            type: "warning",
            text: errors.slice(0, 3).join(" | ") + (errors.length > 3 ? ` ... and ${errors.length - 3} more` : ""),
          });
        }

        if (rows.length === 0) {
          if (errors.length === 0) {
            setCsvImportMsg({
              type: "error",
              text: tt("bulk.csvNoData"),
            });
          }
          e.target.value = "";
          return;
        }

        const defaultCountryCode = guessCountryCode(chats);

        const newContacts = rows
          .map((row, index) => {
            const nameKey = findNameKey(row);
            const rawPhone = findPhoneValue(row);
            const phone = formatPhoneNumber(rawPhone, defaultCountryCode);
            const name = nameKey && row[nameKey]?.trim()
              ? row[nameKey].trim()
              : "N/A";
            return {
              id: `csv_import_${Date.now()}_${index}`,
              name,
              phone,
              status: "pending" as const,
              selected: true,
              source: "csv" as const,
              csvData: row,
              isGroup: false,
              _missingPhone: !phone,
            };
          })
          .filter((c) => c.phone);

        const skippedNoPhone = rows.length - newContacts.length;

        setContacts((prev) => {
          const seenPhones = new Set(prev.map((c) => c.phone));
          const unique = newContacts.filter((c) => !seenPhones.has(c.phone));
          const duplicates = newContacts.length - unique.length;

          // Build result message
          const parts: string[] = [];
          if (unique.length > 0) parts.push(tt("bulk.csvImportSuccess", { count: unique.length, plural: unique.length !== 1 ? "s" : "" }));
          if (duplicates > 0) parts.push(tt("bulk.csvDuplicates", { count: duplicates, plural: duplicates !== 1 ? "s" : "" }));
          if (skippedNoPhone > 0) parts.push(tt("bulk.csvNoPhone", { count: skippedNoPhone, plural: skippedNoPhone !== 1 ? "s" : "" }));

          if (unique.length > 0) {
            setCsvImportMsg({
              type: parts.length > 1 || errors.length > 0 ? "warning" : "success",
              text: parts.join(" · "),
            });
          } else if (duplicates > 0 || skippedNoPhone > 0) {
            setCsvImportMsg({
              type: "warning",
              text: `${tt("bulk.csvNoNew")} ${parts.join(" · ")}`,
            });
          }

          return [...prev, ...unique];
        });
      };
      reader.onerror = () => {
        setCsvImportMsg({
          type: "error",
          text: tt("bulk.csvReadError"),
        });
      };
      reader.readAsText(file);
      e.target.value = "";
    },
    [],
  );

  const handleClearAll = useCallback(() => {
    setContacts([]);
    setCurrentPage(1);
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setContacts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, selected: !c.selected } : c)),
    );
  }, []);

  const handleSend = useCallback((bypassSafeguard = false) => {
    const selectedCount = contacts.filter((c) => c.selected && c.status === "pending").length;

    // Trigger warning if sending to 30+ contacts with low delay (< 6s) or without random delay
    if (!bypassSafeguard && selectedCount >= 30 && (delay < 6 || !randomDelay)) {
      setShowSafeguardWarning(true);
      return;
    }

    const config = {
      messageTemplate,
      attachedFile,
      filePreview,
      isImage,
      delay,
      randomDelay
    };

    if (enableSchedule) {
      if (!scheduleDate || !scheduleTime) return;
      const [hours, minutes] = scheduleTime.split(":").map(Number);
      const targetDate = new Date(scheduleDate);
      targetDate.setHours(hours, minutes, 0, 0);

      const now = new Date();
      if (targetDate.getTime() - now.getTime() <= 0) {
        setError(tt("bulk.scheduleTimeFuture"));
        return;
      }

      scheduleCampaign(config, scheduleDate, scheduleTime);
    } else {
      startCampaign(config);
    }
  }, [
    contacts,
    messageTemplate,
    attachedFile,
    filePreview,
    isImage,
    delay,
    randomDelay,
    enableSchedule,
    scheduleDate,
    scheduleTime,
    scheduleCampaign,
    startCampaign,
    tt
  ]);

  const handleApplySafeSettings = useCallback(() => {
    setDelay(10);
    setRandomDelay(true);
    setShowSafeguardWarning(false);
    
    const config = {
      messageTemplate,
      attachedFile,
      filePreview,
      isImage,
      delay: 10,
      randomDelay: true
    };

    if (enableSchedule) {
      if (!scheduleDate || !scheduleTime) return;
      scheduleCampaign(config, scheduleDate, scheduleTime);
    } else {
      startCampaign(config);
    }
  }, [
    messageTemplate,
    attachedFile,
    filePreview,
    isImage,
    enableSchedule,
    scheduleDate,
    scheduleTime,
    scheduleCampaign,
    startCampaign
  ]);

  const handleStopSending = useCallback(() => {
    stopCampaign();
  }, [stopCampaign]);



  const filtered = contacts.filter((c) => {
    if (statusFilter === "all") return true;
    return c.status === statusFilter;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const startIndex = filtered.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endIndex = Math.min(currentPage * itemsPerPage, filtered.length);

  const allSelected =
    filtered.length > 0 && filtered.every((c) => c.selected);

  const handleSelectAll = useCallback(() => {
    const newValue = !allSelected;
    setContacts((prev) =>
      prev.map((c) => {
        if (filtered.some((p) => p.id === c.id))
          return { ...c, selected: newValue };
        return c;
      }),
    );
  }, [allSelected, filtered]);

  const previewContact = contacts.find((c) => c.selected) || contacts[0];
  const previewText = previewContact
    ? replaceVariables(messageTemplate, previewContact)
    : messageTemplate;

  const stats = {
    total: contacts.length,
    selected: contacts.filter((c) => c.selected).length,
    sent: contacts.filter((c) => c.status === "sent").length,
    failed: contacts.filter((c) => c.status === "failed").length,
    pending: contacts.filter((c) => c.status === "pending").length,
  };

  const filters: {
    key: typeof statusFilter;
    label: string;
    count: number;
    selectedCount: number;
  }[] = [
    { key: "all", label: tt("bulk.all"), count: contacts.length, selectedCount: contacts.filter((c) => c.selected).length },
    { key: "pending", label: tt("bulk.pending"), count: stats.pending, selectedCount: contacts.filter((c) => c.status === "pending" && c.selected).length },
    { key: "sent", label: tt("bulk.sent"), count: stats.sent, selectedCount: contacts.filter((c) => c.status === "sent" && c.selected).length },
    { key: "failed", label: tt("bulk.failed"), count: stats.failed, selectedCount: contacts.filter((c) => c.status === "failed" && c.selected).length },
  ];

  // Clamp current page when itemsPerPage changes
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [itemsPerPage, filtered.length, currentPage]);

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3 px-1">
        <button
          onClick={onBack}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors flex-shrink-0"
          title={tt("bulk.backToChats")}
        >
          <i className="ri-arrow-left-line text-sm"></i>
        </button>
        <h2 className="font-heading text-lg md:text-xl text-foreground-950">
          {tt("bulk.title")}
        </h2>
      </div>

      {/* Mobile Tab Switcher */}
      <div className="lg:hidden flex items-center bg-background-100 rounded-full p-0.5 mb-3">
        <button
          onClick={() => setMobileTab("composer")}
          className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
            mobileTab === "composer"
              ? "bg-white text-foreground-900 shadow-sm"
              : "text-foreground-500 hover:text-foreground-700"
          }`}
        >
          <i className="ri-edit-line text-[11px]"></i>
          {tt("bulk.mobileTabMessage")}
        </button>
        <button
          onClick={() => setMobileTab("contacts")}
          className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
            mobileTab === "contacts"
              ? "bg-white text-foreground-900 shadow-sm"
              : "text-foreground-500 hover:text-foreground-700"
          }`}
        >
          <i className="ri-contacts-line text-[11px]"></i>
          {tt("bulk.mobileTabContacts")}
          {contacts.length > 0 && (
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${mobileTab === "contacts" ? "bg-secondary-500 text-white" : "bg-background-200 text-foreground-500"}`}>
              {contacts.length}
            </span>
          )}
        </button>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-5 flex-1 min-h-0">
        {/* Left: Composer */}
        <div className={`w-full lg:w-[420px] xl:w-[480px] flex-shrink-0 flex flex-col gap-4 overflow-y-auto min-h-0 ${mobileTab !== "composer" ? "hidden lg:flex" : "flex"}`}>
          <div className="bg-background-50 rounded-2xl border border-background-200/50 p-3 md:p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-sm font-semibold text-foreground-950">
                {tt("bulk.messageComposer")}
              </h3>
              <div className="flex items-center bg-background-100 rounded-full p-0.5">
                <button
                  onClick={() => setIsAdvancedMode(false)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    !isAdvancedMode
                      ? "bg-white text-foreground-900 shadow-sm"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  {tt("bulk.simple")}
                </button>
                <button
                  onClick={() => setIsAdvancedMode(true)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    isAdvancedMode
                      ? "bg-white text-foreground-900 shadow-sm"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  {tt("bulk.advanced")}
                </button>
              </div>
            </div>

            <textarea
              ref={textareaRef}
              value={messageTemplate}
              onChange={(e) => setMessageTemplate(e.target.value)}
              placeholder={tt("bulk.messagePlaceholder")}
              className="w-full h-24 md:h-32 rounded-lg border border-background-200/50 bg-background-50 p-3 text-sm text-foreground-900 placeholder:text-foreground-400 resize-none focus:outline-none focus:border-secondary-400 focus:ring-1 focus:ring-secondary-300 transition-colors"
              maxLength={4096}
            />
            <div className="flex items-center justify-end mt-1">
              <span className="text-[10px] text-foreground-400">
                {tt("bulk.charCount", { count: messageTemplate.length })}
              </span>
            </div>

            <p className="text-xs text-foreground-500 mt-2 mb-3">
              <i className="ri-information-line text-[10px] mr-1"></i>
              {tt("bulk.insertVariables")}
            </p>

            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <button
                onClick={() => insertVariable("{{name}}")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-code-line text-[11px]"></i>
                {tt("bulk.name")}
              </button>
              <button
                onClick={() => insertVariable("{{phone}}")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-code-line text-[11px]"></i>
                {tt("bulk.phone")}
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-attachment-line text-[11px]"></i>
                {tt("bulk.attach")}
              </button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                onChange={handleFileSelect}
              />
            </div>

            {attachedFile && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-background-100 mb-3">
                {isImage && filePreview && (
                  <img
                    src={filePreview}
                    alt="preview"
                    className="w-10 h-10 rounded object-cover"
                  />
                )}
                {!isImage && (
                  <div className="w-10 h-10 rounded bg-background-200 flex items-center justify-center">
                    <i className="ri-file-line text-foreground-400 text-sm"></i>
                  </div>
                )}
                <span className="text-xs text-foreground-700 flex-1 truncate">
                  {attachedFile.name}
                </span>
                <button
                  onClick={() => {
                    setAttachedFile(null);
                    setFilePreview("");
                    setIsImage(false);
                  }}
                  className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-background-200 text-foreground-400 cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </div>
            )}

            {isAdvancedMode && (
            <div className="mt-4 pt-4 border-t border-background-200/50">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-timer-line text-accent-600 text-sm"></i>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground-800">
                    {tt("bulk.delayTitle")}
                  </h4>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    {tt("bulk.delayDesc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-5 flex-wrap">
                {[5, 10, 15, 30].map((sec) => (
                  <button
                    key={sec}
                    onClick={() => setDelay(sec)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      delay === sec
                        ? "bg-accent-500 text-white shadow-sm"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {sec}s
                  </button>
                ))}
                <div className="flex items-center bg-background-100 rounded-xl p-0.5">
                  <button
                    onClick={() => setDelay(Math.max(1, delay - 1))}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-500 cursor-pointer transition-colors"
                  >
                    <i className="ri-subtract-line text-xs"></i>
                  </button>
                  <div className="flex items-center gap-1 px-3">
                    <input
                      type="number"
                      min={1}
                      max={300}
                      value={delay}
                      onChange={(e) =>
                        setDelay(
                          Math.max(
                            1,
                            Math.min(300, parseInt(e.target.value) || 1),
                          ),
                        )
                      }
                      className="w-12 bg-transparent text-center text-sm font-semibold text-foreground-900 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <span className="text-xs text-foreground-500 font-medium">
                      {tt("bulk.sec")}
                    </span>
                  </div>
                  <button
                    onClick={() => setDelay(Math.min(300, delay + 1))}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-500 cursor-pointer transition-colors"
                  >
                    <i className="ri-add-line text-xs"></i>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between p-3.5 rounded-2xl bg-background-100/70 border border-background-200/40">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-accent-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shuffle-line text-accent-500 text-base"></i>
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-foreground-800 block">
                      {tt("bulk.randomDelay")}
                    </span>

                  </div>
                </div>
                <div className="flex items-center gap-2.5">
                  <span
                    className={`text-xs font-semibold text-accent-700 bg-accent-100 px-2.5 py-1 rounded-full whitespace-nowrap transition-all duration-300 ${
                      randomDelay
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-90"
                    }`}
                  >
                    {delay}s — {delay * 2}s
                  </span>
                  <button
                    onClick={() => setRandomDelay(!randomDelay)}
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 cursor-pointer transition-all duration-200 ${
                      randomDelay
                        ? "bg-accent-500 border-accent-500"
                        : "bg-white border-background-300 hover:border-accent-400"
                    }`}
                    title={randomDelay ? tt("bulk.switchFixedDelay") : tt("bulk.switchRandomDelay")}
                  >
                    {randomDelay && (
                      <i className="ri-check-line text-white text-[10px] font-bold"></i>
                    )}
                  </button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-background-200/50">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-calendar-schedule-line text-secondary-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-800">
                        {tt("bulk.scheduleTitle")}
                      </h4>

                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (isScheduled) cancelSchedule();
                      else if (!enableSchedule) setEnableSchedule(true);
                      else {
                        setEnableSchedule(false);
                        setScheduleDate("");
                        setScheduleTime("");
                      }
                    }}
                    disabled={isSending}
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 cursor-pointer transition-all duration-200 ${
                      enableSchedule || isScheduled
                        ? "bg-secondary-500 border-secondary-500"
                        : "bg-white border-background-300 hover:border-secondary-400"
                    } ${isSending ? "opacity-40 cursor-not-allowed" : ""}`}
                    title={enableSchedule || isScheduled ? tt("bulk.switchSendNow") : tt("bulk.switchSchedule")}
                  >
                    {(enableSchedule || isScheduled) && (
                      <i className="ri-check-line text-white text-[10px] font-bold"></i>
                    )}
                  </button>
                </div>

                {(enableSchedule || isScheduled) && (
                  <div className="space-y-3">
                    {isScheduled ? (
                      <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-secondary-50 border border-secondary-200/50">
                        <div className="w-9 h-9 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-hourglass-line text-secondary-600 text-base"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-semibold text-foreground-800 block">
                            {tt("bulk.campaignScheduled")}
                          </span>
                          <span className="text-xs text-foreground-500">
                            {(() => {
                              const d = new Date(
                                `${scheduleDate}T${scheduleTime}`,
                              );
                              return d.toLocaleDateString(undefined, {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              }) +
                                " " +
                                d.toLocaleTimeString(undefined, {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                });
                            })()}
                          </span>
                        </div>
                        <button
                          onClick={cancelSchedule}
                          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary-200 text-foreground-500 cursor-pointer transition-colors flex-shrink-0"
                          title={tt("bulk.cancelSchedule")}
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5 block">
                            {tt("bulk.date")}
                          </label>
                          <div className="relative">
                            <input
                              type="date"
                              value={scheduleDate}
                              min={new Date().toISOString().split("T")[0]}
                              onChange={(e) => setScheduleDate(e.target.value)}
                              className="w-full h-10 rounded-xl border border-background-200/50 bg-background-50 px-3 text-sm text-foreground-900 focus:outline-none focus:border-secondary-400 focus:ring-1 focus:ring-secondary-300 transition-colors cursor-pointer [color-scheme:light]"
                            />
                            <i className="ri-calendar-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
                          </div>
                        </div>
                        <div className="flex-1">
                          <label className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-1.5 block">
                            {tt("bulk.time")}
                          </label>
                          <div className="relative">
                            <input
                              type="time"
                              value={scheduleTime}
                              onChange={(e) => setScheduleTime(e.target.value)}
                              className="w-full h-10 rounded-xl border border-background-200/50 bg-background-50 px-3 text-sm text-foreground-900 focus:outline-none focus:border-secondary-400 focus:ring-1 focus:ring-secondary-300 transition-colors cursor-pointer [color-scheme:light]"
                            />
                            <i className="ri-time-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Preview */}
          <div className="bg-background-50 rounded-2xl border border-background-200/50 p-3 md:p-5">
            <h4 className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wider mb-3">
              {tt("bulk.preview")}
            </h4>
            <div className="flex justify-end">
              <div className="bg-accent-100 rounded-2xl rounded-tr-sm p-3 max-w-[85%]">
                <p className="text-sm text-foreground-900 leading-relaxed">
                  {previewText || tt("bulk.previewEmpty")}
                </p>
                <div className="flex items-center justify-end gap-1 mt-1">
                  <span className="text-[10px] text-foreground-500">
                    {tt("bulk.justNow")}
                  </span>
                  <i className="ri-check-double-line text-[10px] text-foreground-400"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Contacts */}
        <div className={`flex-1 flex flex-col min-h-0 bg-background-50 rounded-2xl border border-background-200/50 overflow-hidden ${mobileTab !== "contacts" ? "hidden lg:flex" : "flex"}`}>
          <div className="p-4 border-b border-background-200/50">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-heading text-sm font-semibold text-foreground-950">
                  {tt("bulk.contacts")}
                </h3>
                <p className="text-xs text-foreground-400">
                  {tt("bulk.contactsFormat")}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => csvInputRef.current?.click()}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
                  title={tt("bulk.importCsv")}
                >
                  <i className="ri-upload-cloud-2-line text-sm"></i>
                </button>
                <input
                  type="file"
                  ref={csvInputRef}
                  className="hidden"
                  accept=".csv"
                  onChange={handleCsvImport}
                />
                <button
                  onClick={handleClearAll}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
                  title={tt("bulk.clearAll")}
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
                <span className="hidden lg:contents">
                {isSending ? (
                  <button
                    onClick={handleStopSending}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-red-500 cursor-pointer transition-colors"
                    title={tt("bulk.stopSendingTooltip")}
                  >
                    <i className="ri-stop-fill text-sm"></i>
                  </button>
                ) : (
                  <button
                    onClick={() => handleSend(false)}
                    disabled={
                      isScheduled ||
                      (enableSchedule && (!scheduleDate || !scheduleTime)) ||
                      contacts.filter((c) => c.selected && c.status === "pending")
                        .length === 0
                    }
                    title={
                      isScheduled
                        ? tt("bulk.campaignScheduled")
                        : enableSchedule
                          ? tt("bulk.scheduleSend")
                          : tt("bulk.sendAll")
                    }
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    {isScheduled ? (
                      <i className="ri-hourglass-line text-sm"></i>
                    ) : enableSchedule ? (
                      <i className="ri-calendar-schedule-line text-sm"></i>
                    ) : (
                      <i className="ri-send-plane-line text-sm"></i>
                    )}
                  </button>
                )}
                </span>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {filters.map((f) => (
                <button
                  key={f.key}
                  onClick={() => {
                    setStatusFilter(f.key);
                    setCurrentPage(1);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    statusFilter === f.key
                      ? "bg-secondary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {f.key === "all" ? (
                    <span
                      className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] leading-none ${statusFilter === f.key ? "bg-white/20 text-white" : "bg-background-200 text-foreground-500"}`}
                    >
                      <span className="font-semibold">{f.selectedCount}</span>
                      <span className="opacity-50">/</span>
                      <span className="opacity-70">{f.count}</span>
                    </span>
                  ) : f.key === "pending" ? (
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[10px] leading-none font-semibold ${statusFilter === f.key ? "bg-white/20 text-white" : "bg-background-200 text-foreground-500"}`}
                    >
                      {f.selectedCount}
                    </span>
                  ) : (
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[10px] leading-none font-semibold ${statusFilter === f.key ? "bg-white/20 text-white" : "bg-background-200 text-foreground-500"}`}
                    >
                      {f.count}
                    </span>
                  )}
                  {f.label}
                </button>
              ))}
            </div>

            {/* Schedule Banner */}
            {isScheduled && (
              <div className="mb-2 p-3 rounded-xl bg-secondary-50 border border-secondary-200/50 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-timer-2-line text-secondary-600 text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-semibold text-foreground-800 block">
                    {tt("bulk.sendingMessages")} {" "}
                    {(() => {
                      const d = new Date(`${scheduleDate}T${scheduleTime}`);
                      return d.toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      }) +
                        ", " +
                        d.toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        });
                    })()}
                  </span>
                  <span className="text-[11px] text-foreground-500">
                    {tt("bulk.recipientsWillReceive", { count: stats.selected, plural: stats.selected !== 1 ? "s" : "" })}
                  </span>
                </div>
                <button
                  onClick={cancelSchedule}
                  className="text-xs font-medium text-secondary-700 hover:text-secondary-800 cursor-pointer px-2 py-1 rounded-lg hover:bg-secondary-200 transition-colors whitespace-nowrap"
                >
                  {tt("bulk.cancel")}
                </button>
              </div>
            )}

            {/* Progress */}
            {isSending && (
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-foreground-600 font-medium">
                    {tt("bulk.sendingMessages")}
                  </span>
                  <span className="text-xs text-foreground-500">
                    {sendProgress.current} / {sendProgress.total}
                  </span>
                </div>
                <div className="h-2 bg-background-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all duration-300"
                    style={{
                      width: `${sendProgress.total > 0 ? (sendProgress.current / sendProgress.total) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="mt-2 p-2.5 rounded-xl bg-red-50 border border-red-200/50 text-red-700 text-xs flex items-start gap-2">
                <i className="ri-error-warning-line text-sm flex-shrink-0"></i>
                <span>{error}</span>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto w-5 h-5 flex items-center justify-center rounded-full hover:bg-red-200/50 text-red-500 cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-[10px]"></i>
                </button>
              </div>
            )}
            {csvImportMsg && (
              <div
                className={`mt-2 p-2.5 rounded-xl text-xs flex items-start gap-2 ${
                  csvImportMsg.type === "success"
                    ? "bg-accent-50 border border-accent-200/50 text-accent-800"
                    : csvImportMsg.type === "warning"
                      ? "bg-secondary-50 border border-secondary-200/50 text-secondary-800"
                      : "bg-red-50 border border-red-200/50 text-red-700"
                }`}
              >
                <i
                  className={`flex-shrink-0 text-sm ${
                    csvImportMsg.type === "success"
                      ? "ri-check-line"
                      : csvImportMsg.type === "warning"
                        ? "ri-error-warning-line"
                        : "ri-close-circle-line"
                  }`}
                ></i>
                <span>{csvImportMsg.text}</span>
                <button
                  onClick={() => setCsvImportMsg(null)}
                  className="ml-auto w-5 h-5 flex items-center justify-center rounded-full hover:bg-black/10 text-current cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-[10px]"></i>
                </button>
              </div>
            )}
          </div>

          {/* Table */}
          <div className="flex-1 overflow-y-auto" ref={tableContainerRef}>
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background-50 z-10">
                <tr className="border-b border-background-200/60">
                  <th className="px-4 py-2.5 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected && paginated.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-background-300 cursor-pointer"
                    />
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-foreground-500">
                    {tt("bulk.nameColumn")}
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-foreground-500">
                    {tt("bulk.phoneColumn")}
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-foreground-500">
                    {tt("bulk.statusColumn")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                        <i className="ri-contacts-line text-foreground-400 text-lg"></i>
                      </div>
                      <p className="text-foreground-500 text-xs">
                        {tt("bulk.noContacts")}
                      </p>
                      <p className="text-foreground-400 text-[10px] mt-1">
                        {tt("bulk.importPrompt")}
                      </p>
                    </td>
                  </tr>
                )}
                {paginated.map((contact) => (
                  <tr
                    key={contact.id}
                    className="border-b border-background-100/60 hover:bg-background-100/40 transition-colors"
                  >
                    <td className="px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={contact.selected}
                        onChange={() => toggleSelect(contact.id)}
                        className="rounded border-background-300 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-2.5 text-foreground-900 font-medium text-sm">
                      {contact.name}
                    </td>
                    <td className="px-4 py-2.5 text-foreground-500 font-mono text-xs">
                      {contact.resolvedPhone || contact.phone}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-medium capitalize ${statusColors[contact.status]}`}
                      >
                        {contact.status === "pending" && (
                          <i className="ri-time-line text-[10px] mr-1"></i>
                        )}
                        {contact.status === "sent" && (
                          <i className="ri-check-line text-[10px] mr-1"></i>
                        )}
                        {contact.status === "failed" && (
                          <i className="ri-close-line text-[10px] mr-1"></i>
                        )}
                        {contact.status === "pending" ? tt("bulk.statusPending") : contact.status === "sent" ? tt("bulk.statusSent") : tt("bulk.statusFailed")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-background-200/50 flex items-center justify-between">
            <span className="text-xs text-foreground-500">
              {filtered.length > 0
                ? tt("bulk.pagination", { start: startIndex, end: endIndex, total: filtered.length })
                : tt("bulk.emptyPagination")}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-background-100 text-foreground-500 disabled:opacity-30 cursor-pointer transition-colors"
              >
                <i className="ri-skip-back-line text-xs"></i>
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-background-100 text-foreground-500 disabled:opacity-30 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-s-line text-xs"></i>
              </button>
              <span className="text-xs text-foreground-600 w-7 text-center">
                {currentPage}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages || totalPages === 0}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-background-100 text-foreground-500 disabled:opacity-30 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-right-s-line text-xs"></i>
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-background-100 text-foreground-500 disabled:opacity-30 cursor-pointer transition-colors"
              >
                <i className="ri-skip-forward-line text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar */}
      <div className="lg:hidden flex-shrink-0 mt-3 pt-3 border-t border-background-200/50">
        {/* Progress */}
        {isSending && (
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-foreground-600 font-medium">
                {tt("bulk.sendingMessages")}
              </span>
              <span className="text-xs text-foreground-500">
                {sendProgress.current} / {sendProgress.total}
              </span>
            </div>
            <div className="h-2 bg-background-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-300"
                style={{
                  width: `${sendProgress.total > 0 ? (sendProgress.current / sendProgress.total) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
        )}

        {isScheduled && (
          <div className="mb-2 p-2 rounded-xl bg-secondary-50 border border-secondary-200/50 flex items-center gap-2">
            <i className="ri-timer-2-line text-secondary-600 text-sm flex-shrink-0"></i>
            <span className="text-[11px] text-foreground-700 flex-1">
              {tt("bulk.campaignScheduled")} · {stats.selected} {tt("bulk.mobileTabContacts").toLowerCase()}
            </span>
            <button
              onClick={cancelSchedule}
              className="text-[10px] font-medium text-secondary-700 cursor-pointer px-2 py-1 rounded-md hover:bg-secondary-200 transition-colors whitespace-nowrap"
            >
              {tt("bulk.cancel")}
            </button>
          </div>
        )}

        {error && (
          <div className="mb-2 p-2 rounded-lg bg-red-50 border border-red-200/50 text-red-700 text-[11px] flex items-start gap-1.5">
            <i className="ri-error-warning-line text-xs flex-shrink-0 mt-0.5"></i>
            <span className="flex-1">{error}</span>
            <button
              onClick={() => setError(null)}
              className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-red-200/50 text-red-500 cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-[9px]"></i>
            </button>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="text-xs text-foreground-500 text-center">
            {stats.selected > 0
              ? `${stats.selected} ${tt("bulk.mobileTabContacts").toLowerCase()} ${tt("bulk.pending").toLowerCase()}`
              : tt("bulk.noContacts")}
          </div>
          <div className="flex justify-center">
            {isSending ? (
              <button
                onClick={handleStopSending}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-red-500 text-white text-xs font-semibold hover:bg-red-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-stop-fill text-sm"></i>
                {tt("bulk.stopSending")}
              </button>
            ) : (
              <button
                onClick={() => handleSend(false)}
                disabled={
                  isScheduled ||
                  (enableSchedule && (!scheduleDate || !scheduleTime)) ||
                  contacts.filter((c) => c.selected && c.status === "pending").length === 0
                }
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors whitespace-nowrap"
              >
                {isScheduled ? (
                  <>
                    <i className="ri-hourglass-line text-sm"></i>
                    {tt("bulk.campaignScheduled")}
                  </>
                ) : enableSchedule ? (
                  <>
                    <i className="ri-calendar-schedule-line text-sm"></i>
                    {tt("bulk.scheduleSend")}
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line text-sm"></i>
                    {tt("bulk.sendAll")}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* WhatsApp Safe-Guard Warning Modal */}
      {showSafeguardWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background-950/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-3xl shadow-2xl max-w-md w-full p-6 flex flex-col gap-4 transform scale-100 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100 dark:border-zinc-800">
              <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-flash-fill text-xl animate-pulse"></i>
              </div>
              <div>
                <h3 className="font-heading text-sm font-bold text-zinc-800 dark:text-zinc-100">
                  {tt("bulk.safeguardTitle", { defaultValue: "WhatsApp Account Protection" })}
                </h3>
                <span className="text-[10px] text-zinc-400 dark:text-zinc-500 uppercase font-semibold tracking-wider">
                  {tt("bulk.safeguardSubtitle", { defaultValue: "Anti-Ban Warning" })}
                </span>
              </div>
            </div>

            <div className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed space-y-2 select-text">
              <p>
                {tt("bulk.safeguardWarningText1", {
                  defaultValue: "You are about to send a bulk message campaign to 30+ contacts with fast sending parameters. Sending too fast without random delay puts your WhatsApp account at high risk of being banned by Meta's anti-spam detection systems."
                })}
              </p>
              <div className="p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border border-zinc-100/50 dark:border-zinc-800/80 space-y-1.5 font-medium">
                <p className="text-zinc-700 dark:text-zinc-300 font-semibold">{tt("bulk.safeguardRecommendations", { defaultValue: "Recommended Safe Settings:" })}</p>
                <ul className="list-disc list-inside text-zinc-500 dark:text-zinc-400 space-y-0.5 pl-1.5">
                  <li>{tt("bulk.safeguardRecDelay", { defaultValue: "Set Delay to 10 seconds or more" })}</li>
                  <li>{tt("bulk.safeguardRecRandom", { defaultValue: "Enable Random Delay" })}</li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col gap-2.5 pt-2">
              <button
                onClick={handleApplySafeSettings}
                className="w-full h-10.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-lg shadow-primary-500/15"
              >
                <i className="ri-checkbox-circle-line text-sm"></i>
                {tt("bulk.safeguardApplyBtn", { defaultValue: "Apply Safe Settings (Recommended)" })}
              </button>
              <div className="flex gap-2.5">
                <button
                  onClick={() => {
                    setShowSafeguardWarning(false);
                  }}
                  className="flex-1 h-10.5 rounded-xl border border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 text-xs font-semibold text-zinc-600 dark:text-zinc-300 transition-colors"
                >
                  {tt("common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  onClick={() => {
                    setShowSafeguardWarning(false);
                    handleSend(true); // Bypass safeguard
                  }}
                  className="flex-1 h-10.5 rounded-xl border border-red-200 dark:border-red-900/40 hover:bg-red-50 dark:hover:bg-red-950/20 text-xs font-semibold text-red-650 dark:text-red-400 transition-colors"
                >
                  {tt("bulk.safeguardProceedBtn", { defaultValue: "Send Anyway" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}